import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useGetConsolidatedReportQuery } from "../store/services/reportApi";
import { cellSX } from "../component/common/commonStyle";

import { toast } from "react-toastify";
import { useConstructionOutlinedQuery } from "../store/services/whatsappApi";
import { jwtDecode } from "jwt-decode";
import { useRcsConsolidatedReportQuery } from "../store/services/rcstemplateApi";
import { useClickerConsolidatedReportQuery } from "../store/services/clickerApi";

/* ────────────────────────── Styles ────────────────────────── */
// Shared glass look (mirrors CustomerSmsReport / DownloadCenter)
const filterFieldSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

// Date pickers: 140px (matches the other report pages)
const datePickerSx = {
  width: 140,
  ...filterFieldSx,
};

// Select fields: 140px, glass + hover/focus glow
const selectSx = {
  width: 140,
  ...filterFieldSx,
  "& .MuiOutlinedInput-root": {
    ...filterFieldSx["& .MuiOutlinedInput-root"],
    borderRadius: "8px",
    height: 35,
    background: "rgba(255, 255, 255, 0.8)",
    transition: "all 0.2s ease",
    "&:hover": { boxShadow: "0 4px 12px rgba(14,165,233,0.1)" },
    "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(14,165,233,0.2)" },
  },
  "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "6px 8px" },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#475569" },
};

const ConsolidateReportCount = () => {
  const [startdate, setStartDate] = useState(dayjs());
  const [enddate, setEndDate] = useState(dayjs());
  const [service, setService] = useState("sms");
  const [canFetch, setCanFetch] = useState(true);
  const [dateError, setDateError] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    if (!startdate || !enddate) return;

    const diffDays = enddate.diff(startdate, "day");
    const diffMonths = enddate.diff(startdate, "month", true);

    if (diffDays < 0) {
      setDateError("End date cannot be before start date.");
      setCanFetch(false);
    } else if (diffMonths > 1) {
      setDateError("Date range cannot exceed 1 month.");
      setCanFetch(false);
    } else {
      setDateError("");
      setCanFetch(true);
    }
  }, [startdate, enddate]);

  const getMaxEndDate = () => {
    if (!startdate) return dayjs();

    const oneMonthLater = startdate.add(1, "month");
    return oneMonthLater.isBefore(dayjs()) ? oneMonthLater : dayjs();
  };

  const { data, isLoading, isError } = useGetConsolidatedReportQuery(
    {
      startdate: startdate.format("YYYY-MM-DD"),
      enddate: enddate.format("YYYY-MM-DD"),
    },
    {
      skip:
        !canFetch ||
        !shouldFetch ||
        service === "whatsapp" ||
        service === "rcs" ||
        service === "clicker",
    },
  );

  const reportData = data?.data?.summary || [];
  const totals = data?.data?.totals || {
    successCount: 0,
    failedCount: 0,
    nullCount: 0,
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");

      if (!token) {
        toast.error("Access token not found in local storage");
        return;
      }

      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;

      if (!id) {
        toast.error("Customer ID not found in decoded token");
      } else {
        setCustomerId(id);
      }
    } catch (err) {
      console.error("Error decoding token:", err);
      toast.error("Invalid access token format");
    }
  }, []);

  const {
    data: whatsappData,
    isLoading: whatsappLoading,
    isError: whatsappError,
  } = useConstructionOutlinedQuery(
    {
      customerId: customerId,
      start_date: startdate.format("YYYY-MM-DD"),
      end_date: enddate.format("YYYY-MM-DD"),
    },
    { skip: !canFetch || !shouldFetch || service !== "whatsapp" || !customerId },
  );

  const {
    data: rcsData,
    isLoading: rcsLoading,
    isError: rcsError,
  } = useRcsConsolidatedReportQuery(
    {
      startdate: startdate.format("YYYY-MM-DD"),
      enddate: enddate.format("YYYY-MM-DD"),
    },
    { skip: !canFetch || !shouldFetch || service !== "rcs" },
  );

  const {
    data: clickerData,
    isLoading: clickerLoading,
    isError: clickerError,
  } = useClickerConsolidatedReportQuery(
    {
      startdate: startdate.format("YYYY-MM-DD"),
      enddate: enddate.format("YYYY-MM-DD"),
    },
    { skip: !canFetch || !shouldFetch || service !== "clicker" },
  );

  console.log("whatsappData", whatsappData);
  console.log("rcsData", rcsData);
  console.log("clickerData", clickerData);
  console.log("whatsappLoading:", whatsappLoading, "whatsappError:", whatsappError);

  const smsTotals = data?.data?.totals || {
    sent_count: 0,
    sent_credit: 0,
    delivered_count: 0,
    delivered_credit: 0,
    awaited_count: 0,
    awaited_credit: 0,
    failed_count: 0,
    failed_credit: 0,
  };

  return (
    <Box sx={{ px: 2, py: 3 }}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        Consolidated Report Count
      </Typography>

      {/* ── Glass filter bar (mirrors SMS report page) ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startdate}
              onChange={(date) => {
                setStartDate(date);
                setShouldFetch(false);

                if (
                  enddate &&
                  date &&
                  enddate.diff(date, "month", true) > 1
                ) {
                  setEndDate(date.add(1, "month"));
                }
              }}
              disableFuture
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: datePickerSx } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              value={enddate}
              onChange={(date) => {
                if (
                  startdate &&
                  date &&
                  date.diff(startdate, "month", true) > 1
                ) {
                  toast.error("Date range cannot exceed 1 month");
                  return;
                }
                setEndDate(date);
                setShouldFetch(false);
              }}
              minDate={startdate}
              maxDate={getMaxEndDate()}
              disableFuture
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: datePickerSx } }}
            />
          </LocalizationProvider>

          <FormControl size="small" sx={selectSx}>
            <InputLabel id="service-select-label" sx={{ fontSize: "0.75rem" }}>
              Service
            </InputLabel>
            <Select
              labelId="service-select-label"
              value={service}
              label="Service"
              onChange={(e) => {
                setService(e.target.value);
                setShouldFetch(false);
              }}
            >
              <MenuItem value="sms" sx={{ fontSize: "0.75rem" }}>SMS</MenuItem>
              <MenuItem value="rcs" sx={{ fontSize: "0.75rem" }}>RCS</MenuItem>
              <MenuItem value="whatsapp" sx={{ fontSize: "0.75rem" }}>WhatsApp</MenuItem>
              <MenuItem value="clicker" sx={{ fontSize: "0.75rem" }}>Clicker</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={() => setShouldFetch(true)}
            disabled={!canFetch}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              bgcolor: "#0288d1",
              "&:hover": { bgcolor: "#0277bd" },
              minWidth: "80px",
              ml: 1,
            }}
          >
            Search
          </Button>

          {dateError && (
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "0.7rem", color: "#d32f2f" }}>
                {dateError}
              </Typography>
            </Grid>
          )}
        </Box>
      </Paper>

      {/* ── Data Table ── */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                color: "white",
                fontSize: "0.8rem",
                padding: "6px",
                borderRight: "0.5px solid #1b1b1bff",
                borderBottom: "0.5px solid #1b1b1bff",
              },
            }}
          >
            <TableRow>
              <TableCell>Date</TableCell>
              {service === "rcs" ? (
                <>
                  <TableCell>Sent</TableCell>
                  <TableCell>Delivered</TableCell>
                  <TableCell>Read</TableCell>
                  <TableCell>Executed</TableCell>
                  <TableCell>Failed</TableCell>
                </>
              ) : service === "clicker" ? (
                <>
                  <TableCell>Sent Cnt/Credit</TableCell>
                  <TableCell>Delivered Cnt/Credit</TableCell>
                  <TableCell>Failed Cnt/Credit</TableCell>
                  <TableCell>Awaited Cnt/Credit</TableCell>
                </>
              ) : service === "whatsapp" ? (
                <>
                  <TableCell>Sent Cnt/Credit</TableCell>
                  <TableCell>Delivered Cnt/Credit</TableCell>
                  <TableCell>Awaited Cnt/Credit</TableCell>
                  <TableCell>Failed Cnt/Credit</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Sent Cnt/Credit</TableCell>
                  <TableCell>Delivered Cnt/Credit</TableCell>
                  <TableCell>Failed Cnt/Credit</TableCell>
                  <TableCell>Awaited Cnt/Credit</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {!canFetch ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Please select a valid date range (Max 1 month)
                </TableCell>
              </TableRow>
            ) : !shouldFetch ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Please click Search to fetch data
                </TableCell>
              </TableRow>
            ) : service === "whatsapp" ? (
              // WhatsApp Logic
              whatsappLoading ? (
                <TableRow>
                  <TableCell colSpan={5}>Loading...</TableCell>
                </TableRow>
              ) : whatsappError || !whatsappData ? (
                <TableRow>
                  <TableCell colSpan={5}>No data found or Error loading data</TableCell>
                </TableRow>
              ) : !whatsappData?.data?.summary || whatsappData.data.summary.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>No records found for selected date range</TableCell>
                </TableRow>
              ) : (
                <>
                  {whatsappData.data.summary
                    .filter((item) => {
                      // Filter by date range using string comparison
                      if (!item?.date) return false;
                      const itemDateStr = String(item.date);
                      const startDateStr = startdate.format("YYYY-MM-DD");
                      const endDateStr = enddate.format("YYYY-MM-DD");
                      return itemDateStr >= startDateStr && itemDateStr <= endDateStr;
                    })
                    .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={cellSX}>
                          {dayjs(item.date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.sent_count || 0} / {item.sent_credit || 0}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.delivered_count || 0} / {item.delivered_credit || 0}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.awaited_count || 0} / {item.awaited_credit || 0}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.failed_count || 0} / {item.failed_credit || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  {/* WhatsApp Totals Row */}
                  <TableRow
                    sx={{
                      bgcolor: "#f5f5f5",
                      "& td": { fontWeight: "bold" },
                    }}
                  >
                    <TableCell>Total</TableCell>
                    <TableCell>
                      {whatsappData.data.totals?.sent_count || 0} / {whatsappData.data.totals?.sent_credit || 0}
                    </TableCell>
                    <TableCell>
                      {whatsappData.data.totals?.delivered_count || 0} / {whatsappData.data.totals?.delivered_credit || 0}
                    </TableCell>
                    <TableCell>
                      {whatsappData.data.totals?.awaited_count || 0} / {whatsappData.data.totals?.awaited_credit || 0}
                    </TableCell>
                    <TableCell>
                      {whatsappData.data.totals?.failed_count || 0} / {whatsappData.data.totals?.failed_credit || 0}
                    </TableCell>
                  </TableRow>
                </>
              )
            ) : service === "rcs" ? (
              // RCS Logic
              rcsLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>Loading...</TableCell>
                </TableRow>
              ) : rcsError || !rcsData?.data?.summary ? (
                <TableRow>
                  <TableCell colSpan={6}>No data found</TableCell>
                </TableRow>
              ) : (
                <>
                  {rcsData.data.summary.length > 0 &&
                    rcsData.data.summary.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={cellSX}>
                          {dayjs(item.date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell sx={cellSX}>{item.sentCount}</TableCell>
                        <TableCell sx={cellSX}>{item.deliveredCount}</TableCell>
                        <TableCell sx={cellSX}>{item.readCount}</TableCell>
                        <TableCell sx={cellSX}>{item.executedCount}</TableCell>
                        <TableCell sx={cellSX}>{item.failedCount}</TableCell>
                      </TableRow>
                    ))}
                  {/* RCS Totals Row */}
                  <TableRow
                    sx={{
                      bgcolor: "#f5f5f5",
                      "& td": { fontWeight: "bold" },
                    }}
                  >
                    <TableCell>Total</TableCell>
                    <TableCell>{rcsData.data.totals?.sentCount || 0}</TableCell>
                    <TableCell>
                      {rcsData.data.totals?.deliveredCount || 0}
                    </TableCell>
                    <TableCell>{rcsData.data.totals?.readCount || 0}</TableCell>
                    <TableCell>
                      {rcsData.data.totals?.executedCount || 0}
                    </TableCell>
                    <TableCell>
                      {rcsData.data.totals?.failedCount || 0}
                    </TableCell>
                  </TableRow>
                </>
              )
            ) : service === "clicker" ? (
              // Clicker Logic
              clickerLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>Loading...</TableCell>
                </TableRow>
              ) : clickerError || !clickerData?.data?.summary ? (
                <TableRow>
                  <TableCell colSpan={6}>No data found</TableCell>
                </TableRow>
              ) : (
                <>
                  {clickerData.data.summary.length > 0 &&
                    clickerData.data.summary.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={cellSX}>
                          {dayjs(item.date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.sent_count || 0} / {item.sent_credit || 0}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.delivered_count || 0} / {item.delivered_credit || 0}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.failed_count || 0} / {item.failed_credit || 0}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {item.awaited_count || 0} / {item.awaited_credit || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  {/* Clicker Totals Row */}
                  <TableRow
                    sx={{
                      bgcolor: "#f5f5f5",
                      "& td": { fontWeight: "bold" },
                    }}
                  >
                    <TableCell>Total</TableCell>
                    <TableCell>
                      {clickerData.data.totals?.sent_count || 0} / {clickerData.data.totals?.sent_credit || 0}
                    </TableCell>
                    <TableCell>
                      {clickerData.data.totals?.delivered_count || 0} / {clickerData.data.totals?.delivered_credit || 0}
                    </TableCell>
                    <TableCell>
                      {clickerData.data.totals?.failed_count || 0} / {clickerData.data.totals?.failed_credit || 0}
                    </TableCell>
                    <TableCell>
                      {clickerData.data.totals?.awaited_count || 0} / {clickerData.data.totals?.awaited_credit || 0}
                    </TableCell>
                  </TableRow>
                </>
              )
            ) : // SMS / Other Logic (Default)
              isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>Loading...</TableCell>
                </TableRow>
              ) : isError || reportData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>No data found</TableCell>
                </TableRow>
              ) : (
                <>
                  {reportData.map((item) => (
                    <TableRow key={item.date}>
                      <TableCell sx={cellSX}>
                        {dayjs(item.date).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell sx={cellSX}>
                        {item.sent_count} / {item.sent_credit}
                      </TableCell>

                      <TableCell sx={cellSX}>
                        {item.delivered_count} / {item.delivered_credit}
                      </TableCell>

                      <TableCell sx={cellSX}>
                        {item.failed_count} / {item.failed_credit}
                      </TableCell>

                      <TableCell sx={cellSX}>
                        {item.awaited_count} / {item.awaited_credit}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Totals Row */}
                  <TableRow
                    sx={{
                      bgcolor: "#f5f5f5",
                      "& td": { fontWeight: "bold" },
                    }}
                  >
                    <TableCell>Total</TableCell>

                    <TableCell sx={cellSX}>
                      {smsTotals.sent_count} / {smsTotals.sent_credit}
                    </TableCell>

                    <TableCell sx={cellSX}>
                      {smsTotals.delivered_count} / {smsTotals.delivered_credit}
                    </TableCell>

                    <TableCell sx={cellSX}>
                      {smsTotals.failed_count} / {smsTotals.failed_credit}
                    </TableCell>

                    <TableCell sx={cellSX}>
                      {smsTotals.awaited_count} / {smsTotals.awaited_credit}
                    </TableCell>
                  </TableRow>
                </>
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ConsolidateReportCount;